import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  Box,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {Button, HeadBarProps, MultiSelectBox, Option, PeriodPicker} from "components";
import {OrganizationRef} from "api/organization";
import {Territory} from "api/geoZone";
import {
  deliveryExportClient,
  DeliveryPointsMatricesResultBase,
} from "api/deliveryExport";
import {
  HeatMatrix,
  useDayOfWeekCols,
  useHourRows,
  useMonthCols,
  useYearMonthRows,
} from "app/components/HeatMatrix";
import withConnect from "./withConnect";

type Metric = "deliveries" | "packages";

interface DateInterval {
  startDate: number;
  endDate: number;
}

const useStyles = makeStyles(() => ({
  container: {
    padding: "30px",
    maxWidth: "1800px",
  },
  section: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "20px",
    color: "#353945",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: "16px",
  },
  row: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  groupLabel: {
    fontSize: "14px",
    color: "#353945",
    fontWeight: 500,
    width: "140px",
    flexShrink: 0,
  },
  filter: {
    width: "160px",
    "@media (max-width: 800px)": {
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": {
      height: "44px !important",
    },
  },
  button: {
    height: "44px",
  },
  info: {
    backgroundColor: "rgba(33, 150, 243, 0.08)",
    border: "1px solid #90caf9",
    padding: "16px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  triggered: {
    color: "#4caf50",
    marginTop: "8px",
    fontSize: "14px",
  },
  matricesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    gap: "16px",
    flexWrap: "wrap",
  },
  matricesRow: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
}));

const sortOptions = (options: Option[]): Option[] =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, "fr"));

interface DeliveryPointsExportPageProps {
  carrierRefs: Map<string, OrganizationRef>;
  operatorRefs: Map<string, OrganizationRef>;
  territories: Territory[];
  setHeadBar: (headBar: HeadBarProps) => void;
}

const DeliveryPointsExportPage = (props: DeliveryPointsExportPageProps) => {
  const {carrierRefs, operatorRefs, territories, setHeadBar} = props;
  const classes = useStyles();
  const {t} = useTranslation();

  const [operatorIds, setOperatorIds] = useState<string[]>([]);
  const [carrierIds, setCarrierIds] = useState<string[]>([]);
  const [territoryCodes, setTerritoryCodes] = useState<string[]>([]);
  const [periodStart, setPeriodStart] = useState<DateInterval | undefined>();
  const [periodEnd, setPeriodEnd] = useState<DateInterval | undefined>();
  const [exportLoading, setExportLoading] = useState(false);
  const [exportTriggered, setExportTriggered] = useState(false);
  const [matrices, setMatrices] = useState<DeliveryPointsMatricesResultBase | null>(null);
  const [metric, setMetric] = useState<Metric>("deliveries");

  useEffect(() => {
    setHeadBar({title: t("delivery_points_export_title")});
  }, [setHeadBar, t]);

  const operatorOptions: Option[] = useMemo(
    () => sortOptions(Array.from(operatorRefs.values()).map((org) => ({label: org.displayName, value: org.organizationId}))),
    [operatorRefs]
  );
  const carrierOptions: Option[] = useMemo(
    () => sortOptions(Array.from(carrierRefs.values()).map((org) => ({label: org.displayName, value: org.organizationId}))),
    [carrierRefs]
  );
  const territoryOptions: Option[] = useMemo(
    () => sortOptions((territories ?? []).map((territory) => ({label: territory.name, value: territory.code}))),
    [territories]
  );

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    setExportTriggered(false);
    setMatrices(null);
    try {
      const period =
        periodStart && periodEnd
          ? {startDate: periodStart.startDate, endDate: periodEnd.endDate}
          : {startDate: 0, endDate: Date.now()};

      // Matrices (synchronous, render into the page) and the export (async background email)
      // are independent — fire both in parallel so the user sees the matrices as soon as Mongo
      // finishes, regardless of how long the CSV build takes.
      const [matricesResult] = await Promise.all([
        deliveryExportClient.query.getDeliveryPointsMatrices(
          operatorIds,
          carrierIds,
          territoryCodes,
          period
        ),
        deliveryExportClient.query.exportDeliveryPoints(
          operatorIds,
          carrierIds,
          territoryCodes,
          period
        ),
      ]);
      // Only render when both metric branches are present — the http util swallows server errors
      // and returns the parsed body, which could be an error envelope rather than the expected
      // shape.
      if (matricesResult?.deliveries && matricesResult?.packages) {
        setMatrices(matricesResult);
      }
      setExportTriggered(true);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  }, [operatorIds, carrierIds, territoryCodes, periodStart, periodEnd]);

  const hourRows = useHourRows();
  const dayOfWeekCols = useDayOfWeekCols();
  const monthCols = useMonthCols();
  const yearMonthRows = useYearMonthRows(
    matrices ? matrices[metric].yearMonth : undefined
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.info}>
        <Typography>{t("delivery_points_export_info")}</Typography>
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>{t("delivery_points_export_filters")}</Typography>
        <Box className={classes.filtersRow}>
          <Box className={classes.row}>
            <Typography className={classes.groupLabel}>{t("delivery_points_export_period")}</Typography>
            <PeriodPicker
              id="deliveryPointsPeriodStart"
              placeholder={t("delivery_points_export_period_start")}
              period={periodStart}
              onDateChange={setPeriodStart}
              inputClassName={classes.filter}
              clearable
            />
            <PeriodPicker
              id="deliveryPointsPeriodEnd"
              placeholder={t("delivery_points_export_period_end")}
              period={periodEnd}
              onDateChange={setPeriodEnd}
              inputClassName={classes.filter}
              clearable
            />
          </Box>
          <Box className={classes.row}>
            <MultiSelectBox
              id="delivery-points-operators"
              label={""}
              options={operatorOptions}
              onChange={(ids) => setOperatorIds(ids ?? [])}
              values={operatorIds}
              placeHolder={t("delivery_points_export_operator")}
              className={`${classes.filter} project-filters`}
            />
            <MultiSelectBox
              id="delivery-points-carriers"
              label={""}
              options={carrierOptions}
              onChange={(ids) => setCarrierIds(ids ?? [])}
              values={carrierIds}
              placeHolder={t("delivery_points_export_carrier")}
              className={`${classes.filter} project-filters`}
            />
            <MultiSelectBox
              id="delivery-points-territories"
              label={""}
              options={territoryOptions}
              onChange={(ids) => setTerritoryCodes(ids ?? [])}
              values={territoryCodes}
              placeHolder={t("delivery_points_export_territory")}
              className={`${classes.filter} project-filters`}
            />
          </Box>
          <Button
            className={classes.button}
            onClick={handleExport}
            disabled={exportLoading}
            isLoading={exportLoading}
          >
            {t("delivery_points_export_button")}
          </Button>
        </Box>
        {exportTriggered && (
          <Typography className={classes.triggered}>{t("delivery_points_export_triggered")}</Typography>
        )}
      </Box>

      {matrices && (
        <Box className={classes.section}>
          <Box className={classes.matricesHeader}>
            <Typography className={classes.sectionTitle}>
              {t("delivery_points_export_matrices_title")}
            </Typography>
            <RadioGroup
              row
              value={metric}
              onChange={(e) => setMetric(e.target.value as Metric)}
            >
              <FormControlLabel
                value="deliveries"
                control={<Radio />}
                label={t("delivery_points_export_metric_deliveries")}
              />
              <FormControlLabel
                value="packages"
                control={<Radio />}
                label={t("delivery_points_export_metric_packages")}
              />
            </RadioGroup>
          </Box>
          <Box className={classes.matricesRow}>
            <HeatMatrix
              title={t("delivery_points_export_hour_day_matrix")}
              cells={matrices[metric].hourDayOfWeek}
              rows={hourRows}
              cols={dayOfWeekCols}
            />
            {yearMonthRows.length > 0 && (
              <HeatMatrix
                title={t("delivery_points_export_year_month_matrix")}
                cells={matrices[metric].yearMonth}
                rows={yearMonthRows}
                cols={monthCols}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default withConnect(DeliveryPointsExportPage);
